import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Search,
  Package,
  Layers,
  RefreshCw,
  Tag,
  ImageIcon,
  Info,
  AlertCircle,
  Users,
  ChevronRight,
  Database,
} from 'lucide-react';
import { Portal } from '../types';
import {
  CREATE_CUSTOM_COLLECTION,
  CREATE_CUSTOM_PRODUCT,
  CREATE_CUSTOM_TAG,
  DELETE_CUSTOM_COLLECTION,
  DELETE_CUSTOM_PRODUCT,
  DELETE_CUSTOM_TAG,
  GET_CATALOG_COLLECTIONS,
  GET_CATALOG_PRODUCTS,
  GET_CUSTOM_COLLECTIONS,
  GET_CUSTOM_PRODUCTS,
  GET_CUSTOM_TAGS,
  GET_FSRS,
  SET_CUSTOM_PRODUCT_STATUS,
  UPDATE_CUSTOM_COLLECTION,
  UPDATE_CUSTOM_PRODUCT,
  UPDATE_FSR,
} from '../graphql/queries';
import { SlidePanel } from '../components/SlidePanel';
import { ImageGalleryUploader, ImageUploader } from '../components/ImageUploader';
import { CustomServicesTab } from '../components/CustomServicesTab';
import { useMyStore } from '../hooks/useMyStore';

interface CatalogProduct {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
  code: string | null;
  status: string | null;
  sellingPrice: number | null;
  mrp: number | null;
  discount: number | null;
  image: string | null;
  minimumOrderQty: number | null;
  isMixedPack: boolean;
  isConfigurable: boolean;
  vendor: string | null;
  tags: string[];
}

interface CatalogCollection {
  id: string;
  name: string;
  description: string | null;
  handle: string | null;
  productCount: number;
  image: string | null;
  images: string[];
  source: string | null;
  createdDate: string | null;
  // Custom-store only
  tags?: string[];
  status?: boolean;
}

interface CustomTag {
  id: string;
  name: string;
  color: string | null;
  sortOrder: number;
  status: boolean;
  createdAt: string | null;
}

interface CustomProductOptionT { name: string; value: string }
interface CustomProductVariantT {
  id?: string;
  sku: string | null;
  barcode: string | null;
  options: CustomProductOptionT[];
  mrp: number | null;
  sellingPrice: number | null;
  discount: number;
  stock: number;
  image: string | null;
  status: boolean;
}
interface CustomProductT {
  id: string;
  title: string;
  description: string | null;
  code: string;
  barcode: string | null;
  brand: string | null;
  productType: string | null;
  status: 'active' | 'draft' | 'archived';
  mrp: number | null;
  sellingPrice: number | null;
  discount: number;
  minPrice: number | null;
  maxPrice: number | null;
  currency: string;
  trackInventory: boolean;
  stock: number;
  totalStock: number;
  minimumOrderQty: number;
  image: string | null;
  gallery: string[];
  tags: string[];
  hasVariants: boolean;
  optionTypes: string[];
  variants: CustomProductVariantT[];
  material: string | null;
  careInstructions: string | null;
  gender: string | null;
  sizeChartUrl: string | null;
  weight: number | null;
  dimensions: { length: number | null; width: number | null; height: number | null } | null;
  sortOrder: number;
  createdAt: string | null;
  updatedAt: string | null;
}

interface LiveFsr {
  id: string;
  name: string;
  employeeId: string;
  email: string;
  avatar: string | null;
  color: string | null;
  status: string;
  catalogAccess: 'full' | 'collection' | 'selective';
  catalogCollections: string[];
}

type Tab = 'products' | 'collections' | 'tags' | 'fsr';

interface Props {
  portal: Portal;
  showToast: (message: string, type?: any) => void;
  setTitle: (t: string) => void;
  setSubtitle: (s: string) => void;
}

const PAGE_SIZE = 50;

const formatPrice = (n?: number | null) => {
  if (n == null || isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const initialsOf = (name?: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const ManufacturerCatalog: React.FC<Props> = ({ portal, showToast, setTitle, setSubtitle }) => {
  const { store: myStore } = useMyStore();
  const isCustom = myStore?.storeType === 'custom';
  const isServiceCatalog = isCustom && myStore?.catalogType === 'service';

  const [tab, setTab] = useState<Tab>('products');
  const [search, setSearch] = useState('');
  const [openProduct, setOpenProduct] = useState<CatalogProduct | null>(null);
  const [assignCollection, setAssignCollection] = useState<CatalogCollection | null>(null);
  const [editCollection, setEditCollection] = useState<CatalogCollection | 'new' | null>(null);
  const [editCustomProduct, setEditCustomProduct] = useState<CustomProductT | 'new' | null>(null);

  // Snap to a valid tab if the store type flipped after first render
  useEffect(() => {
    if (!isCustom && tab === 'tags') setTab('collections');
  }, [isCustom, tab]);

  useEffect(() => {
    setTitle('Catalog');
    setSubtitle(
      portal === 'manufacturer'
        ? 'B2B-synced products and collections — read-only here.'
        : 'Products and collections assigned to your team.',
    );
  }, [setTitle, setSubtitle, portal]);

  // Reset search on tab change to avoid carrying it across contexts.
  useEffect(() => {
    setSearch('');
  }, [tab]);

  // ── Products ─────────────────────────────────────────────────
  const productsQuery = useQuery(GET_CATALOG_PRODUCTS, {
    variables: { page: 1, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: isCustom || tab !== 'products',
  });
  const products: CatalogProduct[] = productsQuery.data?.getCatalogProducts?.products ?? [];
  const productsHasMore = !!productsQuery.data?.getCatalogProducts?.hasMore;
  const productsErr = productsQuery.data?.getCatalogProducts?.error
    ? productsQuery.data.getCatalogProducts.message
    : productsQuery.error?.message || null;
  const [productsLoadingMore, setProductsLoadingMore] = useState(false);

  const loadMoreProducts = async () => {
    if (productsLoadingMore || !productsHasMore) return;
    setProductsLoadingMore(true);
    try {
      const nextPage = Math.floor(products.length / PAGE_SIZE) + 1;
      await productsQuery.fetchMore({
        variables: { page: nextPage, limit: PAGE_SIZE, search: search || undefined },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.getCatalogProducts) return prev;
          const seen = new Set<string>();
          const merged = [
            ...(prev?.getCatalogProducts?.products ?? []),
            ...fetchMoreResult.getCatalogProducts.products,
          ].filter((p: any) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
          return { getCatalogProducts: { ...fetchMoreResult.getCatalogProducts, products: merged } };
        },
      });
    } finally {
      setProductsLoadingMore(false);
    }
  };

  // ── Collections ──────────────────────────────────────────────
  // RiEtail stores read from upstream connector; custom stores read from
  // local CustomCollection. Both feed the same `collections` variable.
  const collectionsQuery = useQuery(GET_CATALOG_COLLECTIONS, {
    variables: { page: 1, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: isCustom || (tab !== 'collections' && tab !== 'fsr'),
  });
  const customCollectionsQuery = useQuery(GET_CUSTOM_COLLECTIONS, {
    variables: { search: search || undefined, pageNo: 0, limit: 200 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !isCustom || (tab !== 'collections' && tab !== 'fsr'),
  });

  const collections: CatalogCollection[] = useMemo(() => {
    if (isCustom) {
      return (customCollectionsQuery.data?.getCustomCollections?.items ?? []).map((c: any) => ({
        id: c.id,
        name: c.name,
        description: c.description,
        handle: null,
        productCount: Array.isArray(c.tags) ? c.tags.length : 0,
        image: c.image,
        images: [],
        source: 'custom',
        createdDate: c.createdAt,
        tags: c.tags || [],
        status: c.status,
      }));
    }
    return collectionsQuery.data?.getCatalogCollections?.collections ?? [];
  }, [isCustom, collectionsQuery.data, customCollectionsQuery.data]);

  const collectionsHasMore = !isCustom && !!collectionsQuery.data?.getCatalogCollections?.hasMore;
  const collectionsErr = isCustom
    ? customCollectionsQuery.data?.getCustomCollections?.error
      ? customCollectionsQuery.data.getCustomCollections.message
      : customCollectionsQuery.error?.message || null
    : collectionsQuery.data?.getCatalogCollections?.error
      ? collectionsQuery.data.getCatalogCollections.message
      : collectionsQuery.error?.message || null;
  const collectionsLoading = isCustom ? customCollectionsQuery.loading : collectionsQuery.loading;
  const [collectionsLoadingMore, setCollectionsLoadingMore] = useState(false);

  const loadMoreCollections = async () => {
    if (isCustom) return;
    if (collectionsLoadingMore || !collectionsHasMore) return;
    setCollectionsLoadingMore(true);
    try {
      const nextPage = Math.floor(collections.length / PAGE_SIZE) + 1;
      await collectionsQuery.fetchMore({
        variables: { page: nextPage, limit: PAGE_SIZE, search: search || undefined },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult?.getCatalogCollections) return prev;
          const seen = new Set<string>();
          const merged = [
            ...(prev?.getCatalogCollections?.collections ?? []),
            ...fetchMoreResult.getCatalogCollections.collections,
          ].filter((c: any) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
          return { getCatalogCollections: { ...fetchMoreResult.getCatalogCollections, collections: merged } };
        },
      });
    } finally {
      setCollectionsLoadingMore(false);
    }
  };

  // ── Tags (custom only) ────────────────────────────────────────
  const tagsQuery = useQuery(GET_CUSTOM_TAGS, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !isCustom,
  });
  const tags: CustomTag[] = tagsQuery.data?.getCustomTags?.items ?? [];

  // ── Custom products (custom only) ────────────────────────────
  const customProductsQuery = useQuery(GET_CUSTOM_PRODUCTS, {
    variables: { search: search || undefined, pageNo: 0, limit: 200 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !isCustom || tab !== 'products',
  });
  const customProducts: CustomProductT[] =
    customProductsQuery.data?.getCustomProducts?.items ?? [];
  const customProductsErr = customProductsQuery.data?.getCustomProducts?.error
    ? customProductsQuery.data.getCustomProducts.message
    : customProductsQuery.error?.message || null;

  // ── FSRs (for the FSR Assignment tab) ──────────────────────────
  const fsrQuery = useQuery(GET_FSRS, {
    variables: { page: 1, limit: 200 },
    fetchPolicy: 'cache-and-network',
    skip: tab !== 'fsr',
  });
  const fsrs: LiveFsr[] = (fsrQuery.data?.getFsrs?.fsrs ?? []).map((f: any) => ({
    id: f.id,
    name: f.name,
    employeeId: f.employeeId,
    email: f.email,
    avatar: f.avatar,
    color: f.color,
    status: f.status,
    catalogAccess: f.catalogAccess,
    catalogCollections: f.catalogCollections || [],
  }));

  const refresh = () => {
    if (tab === 'products') {
      if (isCustom) {
        customProductsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
      } else {
        productsQuery.refetch({ page: 1, limit: PAGE_SIZE, search: search || undefined });
      }
    } else if (tab === 'collections') {
      if (isCustom) {
        customCollectionsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
      } else {
        collectionsQuery.refetch({ page: 1, limit: PAGE_SIZE, search: search || undefined });
      }
    } else if (tab === 'tags') {
      tagsQuery.refetch();
    } else fsrQuery.refetch();
    showToast(isCustom ? 'Refreshed' : 'Synced from B2B', 'info');
  };

  return (
    <div className="space-y-6 page-fade-in">
      {/* Source banner */}
      <div className="card p-4 flex items-center gap-4">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            isCustom ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'
          }`}
        >
          <Info size={18} />
        </div>
        <div className="flex-1 text-sm">
          {isServiceCatalog ? (
            <>
              <p className="font-bold text-text">Manage your services</p>
              <p className="text-text3 text-xs mt-0.5">
                Each service has sub-services (with prices) and optional reference links FSRs can use
                while delivering it.
              </p>
            </>
          ) : isCustom ? (
            <>
              <p className="font-bold text-text">Manage your own tags & collections</p>
              <p className="text-text3 text-xs mt-0.5">
                Add tags and group them into collections. Assign collections to field officers in the{' '}
                <span className="font-semibold text-text2">FSR Assignment</span> tab.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-text">Read-only catalog from RiEtail B2B</p>
              <p className="text-text3 text-xs mt-0.5">
                Products and collections sync automatically. Use B2B admin to add or edit. Toggle FSR access in the{' '}
                <span className="font-semibold text-text2">FSR Assignment</span> tab.
              </p>
            </>
          )}
        </div>
        <button
          onClick={refresh}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2 flex items-center gap-2"
        >
          <RefreshCw size={14} /> {isCustom ? 'Refresh' : 'Re-sync'}
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="card p-4 flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          {(
            (isServiceCatalog
              ? [
                  { id: 'products', label: 'Services', icon: Package },
                  { id: 'collections', label: 'Collections', icon: Layers },
                  { id: 'tags', label: 'Tags', icon: Tag },
                  { id: 'fsr', label: 'FSR Assignment', icon: Users },
                ]
              : isCustom
              ? [
                  { id: 'products', label: 'Products', icon: Package },
                  { id: 'collections', label: 'Collections', icon: Layers },
                  { id: 'tags', label: 'Tags', icon: Tag },
                  { id: 'fsr', label: 'FSR Assignment', icon: Users },
                ]
              : [
                  { id: 'products', label: 'Products', icon: Package },
                  { id: 'collections', label: 'Collections', icon: Layers },
                  { id: 'fsr', label: 'FSR Assignment', icon: Users },
                ]) as { id: Tab; label: string; icon: any }[]
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 h-10 rounded-lg text-xs font-bold transition-all flex items-center gap-2
                ${tab === id ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text2 hover:bg-gray-100'}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {tab !== 'fsr' && (
          <div className="relative flex-1 max-w-md ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" size={14} />
            <input
              type="text"
              placeholder={tab === 'products' ? 'Search by product name…' : 'Search collections…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-9 pr-3 bg-gray-50 border border-border rounded-lg text-sm focus:border-primary focus:bg-white outline-none"
            />
          </div>
        )}
      </div>

      {/* Body */}
      {tab === 'products' && (
        isServiceCatalog ? (
          <CustomServicesTab showToast={showToast} tags={tags} />
        ) : isCustom ? (
          <CustomProductsTab
            products={customProducts}
            loading={customProductsQuery.loading}
            error={customProductsErr}
            onCreate={() => setEditCustomProduct('new')}
            onEdit={(p) => setEditCustomProduct(p)}
            onChanged={(m) => {
              showToast(m, 'success');
              customProductsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
            }}
          />
        ) : (
          <ProductsTab
            products={products}
            loading={productsQuery.loading}
            error={productsErr}
            hasMore={productsHasMore}
            loadingMore={productsLoadingMore}
            onLoadMore={loadMoreProducts}
            onOpen={setOpenProduct}
          />
        )
      )}

      {tab === 'collections' && (
        isCustom ? (
          <CustomCollectionsTab
            collections={collections}
            tags={tags}
            loading={collectionsLoading}
            error={collectionsErr}
            onCreate={() => setEditCollection('new')}
            onEdit={(c) => setEditCollection(c)}
            onChanged={(m) => {
              showToast(m, 'success');
              customCollectionsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
              fsrQuery.refetch();
            }}
          />
        ) : (
          <CollectionsTab
            collections={collections}
            loading={collectionsLoading}
            error={collectionsErr}
            hasMore={collectionsHasMore}
            loadingMore={collectionsLoadingMore}
            onLoadMore={loadMoreCollections}
            onAssign={setAssignCollection}
          />
        )
      )}

      {isCustom && tab === 'tags' && (
        <CustomTagsTab
          tags={tags}
          loading={tagsQuery.loading}
          onChanged={(m) => {
            showToast(m, 'success');
            tagsQuery.refetch();
          }}
        />
      )}

      {tab === 'fsr' && (
        <FsrAssignmentTab
          fsrs={fsrs}
          fsrLoading={fsrQuery.loading}
          collections={collections}
          collectionsLoading={collectionsLoading}
          collectionsHasMore={collectionsHasMore}
          collectionsLoadingMore={collectionsLoadingMore}
          onLoadMoreCollections={loadMoreCollections}
          onSaved={(m) => {
            showToast(m, 'success');
            fsrQuery.refetch();
          }}
        />
      )}

      {/* Detail panels */}
      <SlidePanel
        isOpen={!!openProduct}
        onClose={() => setOpenProduct(null)}
        width={520}
        title={openProduct?.name || 'Product'}
        subtitle={openProduct?.code || undefined}
      >
        {openProduct && <ProductDetail product={openProduct} />}
      </SlidePanel>

      <SlidePanel
        isOpen={!!assignCollection}
        onClose={() => setAssignCollection(null)}
        width={520}
        title={assignCollection ? `Assign · ${assignCollection.name}` : 'Assign'}
        subtitle="Pick which field officers get access to this collection"
      >
        {assignCollection && (
          <CollectionAssignPanel
            collection={assignCollection}
            onSaved={(m) => showToast(m, 'success')}
          />
        )}
      </SlidePanel>

      <SlidePanel
        isOpen={!!editCollection}
        onClose={() => setEditCollection(null)}
        width={520}
        title={editCollection === 'new' ? 'New Collection' : `Edit · ${(editCollection as CatalogCollection)?.name ?? ''}`}
        subtitle="Tag-driven product grouping"
      >
        {editCollection && (
          <CustomCollectionEditPanel
            collection={editCollection === 'new' ? null : (editCollection as CatalogCollection)}
            tags={tags}
            onClose={() => setEditCollection(null)}
            onSaved={(m) => {
              showToast(m, 'success');
              setEditCollection(null);
              customCollectionsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
              fsrQuery.refetch();
            }}
          />
        )}
      </SlidePanel>

      <SlidePanel
        isOpen={!!editCustomProduct}
        onClose={() => setEditCustomProduct(null)}
        width={720}
        title={editCustomProduct === 'new' ? 'New Product' : `Edit · ${(editCustomProduct as CustomProductT)?.title ?? ''}`}
        subtitle="Product details, pricing, inventory and variants"
      >
        {editCustomProduct && (
          <CustomProductEditPanel
            product={editCustomProduct === 'new' ? null : (editCustomProduct as CustomProductT)}
            tags={tags}
            onClose={() => setEditCustomProduct(null)}
            onSaved={(m) => {
              showToast(m, 'success');
              setEditCustomProduct(null);
              customProductsQuery.refetch({ search: search || undefined, pageNo: 0, limit: 200 });
            }}
          />
        )}
      </SlidePanel>
    </div>
  );
};

// ── Products tab ─────────────────────────────────────────────────
const ProductsTab: React.FC<{
  products: CatalogProduct[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onOpen: (p: CatalogProduct) => void;
}> = ({ products, loading, error, hasMore, loadingMore, onLoadMore, onOpen }) => {
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertCircle size={16} /> Couldn't load products: {error}
      </div>
    );
  }
  if (loading && products.length === 0) {
    return <div className="card p-12 text-center text-text3">Syncing products from B2B…</div>;
  }
  if (products.length === 0) {
    return <div className="card p-12 text-center text-text3">No products found.</div>;
  }
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((p) => (
          <button
            key={p.id}
            onClick={() => onOpen(p)}
            className="card p-4 text-left flex flex-col gap-3 hover:border-primary/50 hover:shadow-lg transition-all"
          >
            <div className="aspect-square rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
              {p.image ? (
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon size={28} className="text-text3" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-text3">{p.code || '—'}</span>
                {p.type && p.type !== 'single' && (
                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                    {p.type}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-bold text-text leading-snug line-clamp-2 mb-1">{p.name}</h3>
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-black text-primary">{formatPrice(p.sellingPrice)}</span>
                {p.mrp != null && p.mrp !== p.sellingPrice && (
                  <span className="text-[11px] text-text3 line-through">{formatPrice(p.mrp)}</span>
                )}
                {p.discount != null && p.discount > 0 && (
                  <span className="text-[10px] font-bold text-emerald-600 ml-auto">
                    {Math.round(p.discount)}% off
                  </span>
                )}
              </div>
              {p.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {p.tags.slice(0, 2).map((t) => (
                    <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-100 text-text2 rounded">
                      {t}
                    </span>
                  ))}
                  {p.tags.length > 2 && (
                    <span className="text-[9px] font-bold text-text3">+{p.tags.length - 2}</span>
                  )}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {hasMore && (
        <div className="pt-4 flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="h-11 px-6 rounded-full border border-border bg-white text-text2 font-bold text-xs hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <RefreshCw size={12} className="animate-spin" /> Loading…
              </>
            ) : (
              <>Show more</>
            )}
          </button>
        </div>
      )}
    </>
  );
};

// ── Collections tab ──────────────────────────────────────────────
const CollectionsTab: React.FC<{
  collections: CatalogCollection[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onAssign: (c: CatalogCollection) => void;
}> = ({ collections, loading, error, hasMore, loadingMore, onLoadMore, onAssign }) => {
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertCircle size={16} /> Couldn't load collections: {error}
      </div>
    );
  }
  if (loading && collections.length === 0) {
    return <div className="card p-12 text-center text-text3">Syncing collections from B2B…</div>;
  }
  if (collections.length === 0) {
    return <div className="card p-12 text-center text-text3">No collections found.</div>;
  }
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {collections.map((c) => (
          <button
            key={c.id}
            onClick={() => onAssign(c)}
            className="group relative bg-white border border-border rounded-2xl overflow-hidden text-left flex items-stretch hover:border-primary/40 hover:shadow-md transition-all"
          >
            {/* Left: single hero image */}
            <div className="w-28 shrink-0 relative bg-gradient-to-br from-indigo-100 to-purple-100 overflow-hidden">
              {(() => {
                const hero = c.image || c.images?.[0] || null;
                return hero ? (
                  <img
                    src={hero}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-black text-primary/40">{initialsOf(c.name)}</span>
                  </div>
                );
              })()}
            </div>

            {/* Right: content */}
            <div className="flex-1 min-w-0 p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-black text-text leading-snug line-clamp-2 mb-1.5">{c.name}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.source && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-primary text-[10px] font-black uppercase tracking-widest rounded-full border border-indigo-100">
                      <Tag size={9} />
                      {c.source}
                    </span>
                  )}
                  {c.handle && (
                    <span className="text-[10px] font-mono text-text3 truncate">/{c.handle}</span>
                  )}
                </div>
              </div>
              <div className="h-9 px-3 rounded-lg bg-primary/10 group-hover:bg-primary group-hover:text-white text-primary font-bold text-xs flex items-center gap-1.5 shrink-0 transition-colors">
                <Users size={13} /> Assign
              </div>
            </div>
          </button>
        ))}
      </div>

      {hasMore && (
        <div className="pt-4 flex justify-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="h-11 px-6 rounded-full border border-border bg-white text-text2 font-bold text-xs hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <RefreshCw size={12} className="animate-spin" /> Loading…
              </>
            ) : (
              <>Show more</>
            )}
          </button>
        </div>
      )}
    </>
  );
};

// ── FSR Assignment tab ───────────────────────────────────────────
const FsrAssignmentTab: React.FC<{
  fsrs: LiveFsr[];
  fsrLoading: boolean;
  collections: CatalogCollection[];
  collectionsLoading: boolean;
  collectionsHasMore: boolean;
  collectionsLoadingMore: boolean;
  onLoadMoreCollections: () => void;
  onSaved: (m: string) => void;
}> = ({
  fsrs,
  fsrLoading,
  collections,
  collectionsLoading,
  collectionsHasMore,
  collectionsLoadingMore,
  onLoadMoreCollections,
  onSaved,
}) => {
  const [selectedFsrId, setSelectedFsrId] = useState<string | null>(null);
  const [updateFsr, { loading: saving }] = useMutation(UPDATE_FSR);

  useEffect(() => {
    if (!selectedFsrId && fsrs.length > 0) setSelectedFsrId(fsrs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsrs.length]);

  const selectedFsr = useMemo(
    () => fsrs.find((f) => f.id === selectedFsrId) ?? null,
    [fsrs, selectedFsrId],
  );

  const setAccess = async (catalogAccess: 'full' | 'collection', catalogCollections: string[]) => {
    if (!selectedFsr) return;
    const { data } = await updateFsr({
      variables: { id: selectedFsr.id, catalogAccess, catalogCollections },
    });
    const out = data?.updateFsr;
    if (out?.error) {
      onSaved(out.message || 'Failed to update');
      return;
    }
    onSaved('Catalog access updated');
  };

  const toggleCollection = async (collectionId: string) => {
    if (!selectedFsr) return;
    const current = new Set(selectedFsr.catalogCollections);
    if (current.has(collectionId)) current.delete(collectionId);
    else current.add(collectionId);
    await setAccess('collection', Array.from(current));
  };

  const giveFullAccess = async () => {
    if (!selectedFsr) return;
    await setAccess('full', []);
  };

  const removeAll = async () => {
    if (!selectedFsr) return;
    await setAccess('collection', []);
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      <aside className="col-span-4 card p-3 flex flex-col max-h-[700px]">
        <header className="px-2 pt-1 pb-3 mb-1 border-b border-border">
          <h3 className="text-sm font-black text-text uppercase tracking-widest flex items-center gap-2">
            <Users size={14} className="text-primary" /> Field Officers
          </h3>
          <p className="text-[11px] text-text3">Pick an officer to manage their catalog.</p>
        </header>
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1.5 px-1">
          {fsrLoading && fsrs.length === 0 ? (
            <div className="text-center text-text3 text-sm py-12">Loading FSRs…</div>
          ) : fsrs.length === 0 ? (
            <div className="text-center text-text3 text-sm py-12">
              No field users yet. Add some in <span className="font-bold">Field Users</span>.
            </div>
          ) : (
            fsrs.map((f) => {
              const isSelected = f.id === selectedFsrId;
              const summary =
                f.catalogAccess === 'full'
                  ? 'Full catalog'
                  : `${f.catalogCollections.length} ${f.catalogCollections.length === 1 ? 'collection' : 'collections'}`;
              return (
                <button
                  key={f.id}
                  onClick={() => setSelectedFsrId(f.id)}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-colors flex items-center gap-3 ${
                    isSelected ? 'bg-indigo-50 border-primary' : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0"
                    style={{ backgroundColor: f.color || '#4F46E5' }}
                  >
                    {f.avatar || initialsOf(f.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-text truncate">{f.name}</div>
                    <div className="text-[10px] text-text3 truncate">{summary}</div>
                  </div>
                  <ChevronRight size={14} className={isSelected ? 'text-primary' : 'text-text3'} />
                </button>
              );
            })
          )}
        </div>
      </aside>

      <main className="col-span-8 card flex flex-col max-h-[700px]">
        {!selectedFsr ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-text3 mb-3">
              <Users size={28} />
            </div>
            <h3 className="text-sm font-bold text-text">Pick an officer</h3>
            <p className="text-xs text-text3 max-w-xs mt-1">
              Select a Field Sales Representative to configure their catalog access.
            </p>
          </div>
        ) : (
          <>
            <header className="p-5 border-b border-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-sm shadow shrink-0"
                  style={{ backgroundColor: selectedFsr.color || '#4F46E5' }}
                >
                  {selectedFsr.avatar || initialsOf(selectedFsr.name)}
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-black text-text truncate">{selectedFsr.name}</h3>
                  <p className="text-xs text-text3">
                    {selectedFsr.employeeId} · {selectedFsr.email}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={giveFullAccess}
                  disabled={saving || selectedFsr.catalogAccess === 'full'}
                  className="h-9 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold disabled:opacity-60"
                >
                  Give Full Access
                </button>
                <button
                  onClick={removeAll}
                  disabled={
                    saving ||
                    (selectedFsr.catalogAccess === 'collection' && selectedFsr.catalogCollections.length === 0)
                  }
                  className="h-9 px-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-bold disabled:opacity-60"
                >
                  Remove All
                </button>
              </div>
            </header>

            <div className="px-5 py-3 border-b border-border bg-gray-50/50">
              <div className="flex items-center gap-3 text-xs">
                <Database size={14} className="text-primary" />
                <span className="font-bold text-text">Current access:</span>
                {selectedFsr.catalogAccess === 'full' ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold uppercase tracking-widest text-[10px]">
                    Full Catalog
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-indigo-100 text-primary rounded font-bold uppercase tracking-widest text-[10px]">
                    {selectedFsr.catalogCollections.length}{' '}
                    {selectedFsr.catalogCollections.length === 1 ? 'collection' : 'collections'}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-2">
              {collectionsLoading && collections.length === 0 ? (
                <div className="text-center text-text3 text-sm py-12">Loading collections…</div>
              ) : collections.length === 0 ? (
                <div className="text-center text-text3 text-sm py-12">No collections to assign yet.</div>
              ) : (
                <>
                  {collections.map((c) => {
                    const isAssigned =
                      selectedFsr.catalogAccess === 'full' || selectedFsr.catalogCollections.includes(c.id);
                    const fullAccess = selectedFsr.catalogAccess === 'full';
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleCollection(c.id)}
                        disabled={saving || fullAccess}
                        title={fullAccess ? 'Disable full access first to toggle individual collections' : ''}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors
                          ${isAssigned ? 'border-primary bg-indigo-50/60' : 'border-border bg-white hover:bg-gray-50'}
                          ${fullAccess ? 'opacity-70' : ''} disabled:cursor-not-allowed`}
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 shrink-0 relative">
                          {(() => {
                            const hero = c.image || c.images?.[0] || null;
                            return hero ? (
                              <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-xs font-black text-primary/40">{initialsOf(c.name)}</span>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-text truncate">{c.name}</div>
                          {c.source && (
                            <div className="text-[10px] text-text3 uppercase tracking-widest">{c.source}</div>
                          )}
                        </div>
                        <div
                          className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors
                            ${isAssigned ? 'bg-primary' : 'bg-gray-300'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                              ${isAssigned ? 'translate-x-6' : 'translate-x-1'}`}
                          />
                        </div>
                      </button>
                    );
                  })}

                  {collectionsHasMore && (
                    <div className="pt-3 flex justify-center">
                      <button
                        onClick={onLoadMoreCollections}
                        disabled={collectionsLoadingMore}
                        className="h-9 px-4 rounded-full border border-border bg-white text-text2 font-bold text-xs hover:bg-gray-50 disabled:opacity-60 flex items-center gap-2"
                      >
                        {collectionsLoadingMore ? (
                          <>
                            <RefreshCw size={12} className="animate-spin" /> Loading…
                          </>
                        ) : (
                          <>Show more collections</>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// ── Detail panels ────────────────────────────────────────────────
const ProductDetail: React.FC<{ product: CatalogProduct }> = ({ product }) => (
  <div className="space-y-5">
    <div className="aspect-square rounded-2xl bg-gray-100 overflow-hidden flex items-center justify-center">
      {product.image ? (
        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
      ) : (
        <ImageIcon size={48} className="text-text3" />
      )}
    </div>

    <div>
      <div className="flex items-center gap-2 mb-2">
        {product.code && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-text3">{product.code}</span>
        )}
        {product.type && product.type !== 'single' && (
          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
            {product.type}
          </span>
        )}
        {product.status && (
          <span
            className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
              product.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {product.status}
          </span>
        )}
      </div>
      <h2 className="text-xl font-black text-text">{product.name}</h2>
      {product.description && <p className="text-sm text-text2 mt-2 leading-relaxed">{product.description}</p>}
    </div>

    <div className="grid grid-cols-3 gap-3">
      <Metric label="Selling" value={formatPrice(product.sellingPrice)} highlight />
      <Metric label="MRP" value={formatPrice(product.mrp)} />
      <Metric
        label="Discount"
        value={product.discount != null ? `${Math.round(product.discount)}%` : '—'}
      />
    </div>

    <Section label="Details">
      <Row label="Vendor" value={product.vendor || '—'} />
      <Row label="Min Order Qty" value={product.minimumOrderQty?.toString() || '—'} />
      <Row label="Mixed Pack" value={product.isMixedPack ? 'Yes' : 'No'} />
      <Row label="Configurable" value={product.isConfigurable ? 'Yes' : 'No'} />
    </Section>

    {product.tags.length > 0 && (
      <Section label="Tags / Collections">
        <div className="flex flex-wrap gap-1.5">
          {product.tags.map((t) => (
            <span
              key={t}
              className="text-[11px] font-bold px-2.5 py-1 bg-indigo-50 text-primary rounded-full border border-indigo-100"
            >
              {t}
            </span>
          ))}
        </div>
      </Section>
    )}
  </div>
);

const Metric: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`p-3 rounded-xl border ${highlight ? 'border-primary/30 bg-indigo-50' : 'border-border bg-gray-50'}`}>
    <div className="text-[9px] font-bold uppercase tracking-widest text-text3 mb-1">{label}</div>
    <div className={`text-sm font-black ${highlight ? 'text-primary' : 'text-text'}`}>{value}</div>
  </div>
);

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <section>
    <h3 className="text-[10px] font-bold uppercase tracking-widest text-text3 mb-2">{label}</h3>
    <div className="space-y-1.5">{children}</div>
  </section>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-lg border border-border">
    <span className="text-xs text-text3">{label}</span>
    <span className="text-sm font-semibold text-text">{value}</span>
  </div>
);

// ── Collection Assign Panel (per-collection FSR toggles) ─────────
const CollectionAssignPanel: React.FC<{
  collection: CatalogCollection;
  onSaved: (m: string) => void;
}> = ({ collection, onSaved }) => {
  const fsrQuery = useQuery(GET_FSRS, {
    variables: { page: 1, limit: 200 },
    fetchPolicy: 'cache-and-network',
  });
  const fsrs: LiveFsr[] = (fsrQuery.data?.getFsrs?.fsrs ?? []).map((f: any) => ({
    id: f.id,
    name: f.name,
    employeeId: f.employeeId,
    email: f.email,
    avatar: f.avatar,
    color: f.color,
    status: f.status,
    catalogAccess: f.catalogAccess,
    catalogCollections: f.catalogCollections || [],
  }));

  const [updateFsr, { loading: saving }] = useMutation(UPDATE_FSR);

  const isAssigned = (f: LiveFsr) =>
    f.catalogAccess === 'full' || f.catalogCollections.includes(collection.id);

  const toggleFsr = async (f: LiveFsr) => {
    // FSRs on full access already have access. Don't auto-flip them to
    // selective from this panel — surface a clear toast instead.
    if (f.catalogAccess === 'full') {
      onSaved(`${f.name} has full catalog access. Switch to collection-mode in the FSR Assignment tab to scope down.`);
      return;
    }
    const next = new Set(f.catalogCollections);
    if (next.has(collection.id)) next.delete(collection.id);
    else next.add(collection.id);
    const { data } = await updateFsr({
      variables: {
        id: f.id,
        catalogAccess: 'collection',
        catalogCollections: Array.from(next),
      },
    });
    if (data?.updateFsr?.error) {
      onSaved(data.updateFsr.message || 'Failed to update');
      return;
    }
    await fsrQuery.refetch();
  };

  const assignAll = async () => {
    const targets = fsrs.filter((f) => f.catalogAccess !== 'full' && !f.catalogCollections.includes(collection.id));
    if (targets.length === 0) {
      onSaved('Already assigned to every officer');
      return;
    }
    await Promise.all(
      targets.map((f) =>
        updateFsr({
          variables: {
            id: f.id,
            catalogAccess: 'collection',
            catalogCollections: Array.from(new Set([...f.catalogCollections, collection.id])),
          },
        }),
      ),
    );
    await fsrQuery.refetch();
    onSaved(`Assigned to ${targets.length} ${targets.length === 1 ? 'officer' : 'officers'}`);
  };

  const removeAll = async () => {
    const targets = fsrs.filter((f) => f.catalogAccess !== 'full' && f.catalogCollections.includes(collection.id));
    if (targets.length === 0) {
      onSaved('No selective-access officers had this collection');
      return;
    }
    await Promise.all(
      targets.map((f) =>
        updateFsr({
          variables: {
            id: f.id,
            catalogAccess: 'collection',
            catalogCollections: f.catalogCollections.filter((id) => id !== collection.id),
          },
        }),
      ),
    );
    await fsrQuery.refetch();
    onSaved(`Removed from ${targets.length} ${targets.length === 1 ? 'officer' : 'officers'}`);
  };

  const assignedCount = fsrs.filter(isAssigned).length;

  return (
    <div className="space-y-5 pb-6">
      {/* Collection summary header */}
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 shrink-0 relative">
          {(() => {
            const hero = collection.image || collection.images?.[0] || null;
            return hero ? (
              <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-black text-primary/40">{initialsOf(collection.name)}</span>
              </div>
            );
          })()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-black text-text truncate">{collection.name}</h3>
          <p className="text-xs text-text3">
            {assignedCount} of {fsrs.length} {fsrs.length === 1 ? 'officer has' : 'officers have'} access
          </p>
        </div>
      </div>

      {/* Bulk buttons */}
      <div className="flex gap-2">
        <button
          onClick={assignAll}
          disabled={saving || fsrs.length === 0}
          className="flex-1 h-10 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold disabled:opacity-60"
        >
          Give Access to All FSRs
        </button>
        <button
          onClick={removeAll}
          disabled={saving || assignedCount === 0}
          className="flex-1 h-10 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-xs font-bold disabled:opacity-60"
        >
          Remove from All
        </button>
      </div>

      {/* FSR list */}
      <div className="space-y-2">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-text3 px-1">Field officers</h4>
        {fsrQuery.loading && fsrs.length === 0 ? (
          <div className="text-center text-text3 text-sm py-8">Loading officers…</div>
        ) : fsrs.length === 0 ? (
          <div className="text-center text-text3 text-sm py-8">
            No field users yet. Add some in <span className="font-bold">Field Users</span>.
          </div>
        ) : (
          fsrs.map((f) => {
            const assigned = isAssigned(f);
            const fullAccess = f.catalogAccess === 'full';
            return (
              <div
                key={f.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors
                  ${assigned ? 'border-primary bg-indigo-50/60' : 'border-border bg-white'}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0"
                  style={{ backgroundColor: f.color || '#4F46E5' }}
                >
                  {f.avatar || initialsOf(f.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-text truncate">{f.name}</span>
                    {fullAccess && (
                      <span
                        className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded"
                        title="This officer has full catalog access"
                      >
                        Full
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-text3 truncate">{f.employeeId}</div>
                </div>
                <button
                  onClick={() => toggleFsr(f)}
                  disabled={saving || fullAccess}
                  title={fullAccess ? 'Officer has full catalog access' : ''}
                  className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed
                    ${assigned ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                      ${assigned ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ── Custom Collections tab ──────────────────────────────────────
const CustomCollectionsTab: React.FC<{
  collections: CatalogCollection[];
  tags: CustomTag[];
  loading: boolean;
  error: string | null;
  onCreate: () => void;
  onEdit: (c: CatalogCollection) => void;
  onChanged: (m: string) => void;
}> = ({ collections, tags, loading, error, onCreate, onEdit, onChanged }) => {
  const [deleteCollection] = useMutation(DELETE_CUSTOM_COLLECTION);
  const [pendingDelete, setPendingDelete] = useState<CatalogCollection | null>(null);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { data } = await deleteCollection({ variables: { id: pendingDelete.id } });
    const r = data?.deleteCustomCollection;
    setPendingDelete(null);
    onChanged(r?.error ? r.message || 'Failed to delete' : r?.message || 'Collection removed');
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertCircle size={16} /> Couldn't load collections: {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-text3">
          {collections.length} {collections.length === 1 ? 'collection' : 'collections'}
          {tags.length > 0 && ` · ${tags.length} ${tags.length === 1 ? 'tag' : 'tags'} available`}
        </div>
        <button onClick={onCreate} className="btn-primary flex items-center gap-2">
          <Layers size={14} /> New Collection
        </button>
      </div>

      {loading && collections.length === 0 ? (
        <div className="card p-12 text-center text-text3">Loading collections…</div>
      ) : collections.length === 0 ? (
        <div className="card p-12 text-center text-text3 flex flex-col items-center gap-3">
          <Database size={28} className="text-text3" />
          <div className="text-sm font-bold text-text">No collections yet</div>
          <div className="text-xs text-text3 max-w-xs">
            Create tags first in the <span className="font-bold">Tags</span> tab, then group them into collections.
          </div>
          {tags.length > 0 && (
            <button onClick={onCreate} className="btn-primary mt-2 flex items-center gap-2">
              <Layers size={14} /> Create your first collection
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {collections.map((c) => (
            <div
              key={c.id}
              className="card p-4 flex flex-col gap-3 hover:border-primary/50 hover:shadow-lg transition-all"
            >
              <div className="aspect-[16/9] rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                {c.image ? (
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <Layers size={28} className="text-text3" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="text-sm font-bold text-text truncate">{c.name}</h3>
                  {c.status === false && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-200 text-text2 rounded uppercase">
                      Inactive
                    </span>
                  )}
                </div>
                {c.description && (
                  <p className="text-[11px] text-text3 line-clamp-2 mb-2">{c.description}</p>
                )}
                {Array.isArray(c.tags) && c.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {c.tags.slice(0, 5).map((t) => (
                      <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 bg-indigo-50 text-primary rounded">
                        #{t}
                      </span>
                    ))}
                    {c.tags.length > 5 && (
                      <span className="text-[9px] font-bold text-text3">+{c.tags.length - 5}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2 border-t border-border">
                <button
                  onClick={() => onEdit(c)}
                  className="flex-1 h-8 px-3 rounded-lg border border-border text-[12px] font-semibold text-text2 hover:bg-gray-50 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => setPendingDelete(c)}
                  className="h-8 px-3 rounded-lg border border-border text-[12px] font-semibold text-red-600 hover:bg-red-50 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-base font-bold text-text mb-1">Delete collection?</h3>
            <p className="text-xs text-text3 mb-5">
              "{pendingDelete.name}" will be removed. Any FSR access tied to it will need to be re-assigned.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Custom Tags tab ─────────────────────────────────────────────
const CustomTagsTab: React.FC<{
  tags: CustomTag[];
  loading: boolean;
  onChanged: (m: string) => void;
}> = ({ tags, loading, onChanged }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#4F46E5');
  const [error, setError] = useState<string | null>(null);
  const [createTag, { loading: creating }] = useMutation(CREATE_CUSTOM_TAG);
  const [deleteTag] = useMutation(DELETE_CUSTOM_TAG);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Tag name is required');
      return;
    }
    try {
      const { data } = await createTag({ variables: { input: { name: name.trim(), color } } });
      const r = data?.createCustomTag;
      if (r?.error) {
        setError(r.message || 'Failed');
        return;
      }
      setName('');
      onChanged(r?.message || 'Tag added');
    } catch (err: any) {
      setError(err?.message || 'Failed');
    }
  };

  const remove = async (tag: CustomTag) => {
    if (!window.confirm(`Remove tag "${tag.name}"?`)) return;
    const { data } = await deleteTag({ variables: { id: tag.id } });
    const r = data?.deleteCustomTag;
    onChanged(r?.error ? r.message || 'Failed to delete' : r?.message || 'Tag removed');
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      <section className="col-span-4 card p-5 h-fit">
        <header className="mb-4">
          <h3 className="text-sm font-black text-text uppercase tracking-widest flex items-center gap-2">
            <Tag size={14} className="text-primary" /> Add Tag
          </h3>
          <p className="text-[11px] text-text3 mt-1">
            Tags group products into collections. Use simple names like "premium" or "summer-2025".
          </p>
        </header>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Name</label>
            <input
              className="input-field mt-1 w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. premium"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Color</label>
            <div className="mt-1 flex items-center gap-3">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 rounded-lg border border-border cursor-pointer"
              />
              <span className="text-xs text-text2 font-mono">{color}</span>
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <button type="submit" disabled={creating} className="btn-primary w-full disabled:opacity-60">
            {creating ? 'Adding…' : 'Add Tag'}
          </button>
        </form>
      </section>

      <section className="col-span-8 card p-5">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black text-text uppercase tracking-widest">Tag Pool</h3>
            <p className="text-[11px] text-text3 mt-1">
              {tags.length} {tags.length === 1 ? 'tag' : 'tags'} available
            </p>
          </div>
        </header>
        {loading && tags.length === 0 ? (
          <div className="text-center text-text3 text-sm py-12">Loading tags…</div>
        ) : tags.length === 0 ? (
          <div className="text-center text-text3 text-sm py-12">
            No tags yet. Add your first tag from the panel on the left.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <div
                key={t.id}
                className="group flex items-center gap-2 pl-3 pr-1.5 h-9 rounded-full border border-border bg-white text-sm font-semibold text-text2 hover:border-primary/40 transition"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: t.color || '#94A3B8' }}
                />
                <span>#{t.name}</span>
                <button
                  onClick={() => remove(t)}
                  className="ml-1 w-6 h-6 rounded-full hover:bg-red-50 hover:text-red-600 text-text3 flex items-center justify-center transition"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// ── Custom Collection edit panel (create / update) ──────────────
const CustomCollectionEditPanel: React.FC<{
  collection: CatalogCollection | null;
  tags: CustomTag[];
  onClose: () => void;
  onSaved: (m: string) => void;
}> = ({ collection, tags, onClose, onSaved }) => {
  const isEdit = !!collection;
  const [form, setForm] = useState({
    name: collection?.name || '',
    description: collection?.description || '',
    tags: collection?.tags ? [...collection.tags] : ([] as string[]),
  });
  const [error, setError] = useState<string | null>(null);
  const [createCollection, { loading: creating }] = useMutation(CREATE_CUSTOM_COLLECTION);
  const [updateCollection, { loading: updating }] = useMutation(UPDATE_CUSTOM_COLLECTION);

  const saving = creating || updating;

  const toggleTag = (name: string) => {
    setForm((f) =>
      f.tags.includes(name) ? { ...f, tags: f.tags.filter((t) => t !== name) } : { ...f, tags: [...f.tags, name] }
    );
  };

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) {
      setError('Collection name is required');
      return;
    }
    if (form.tags.length === 0) {
      setError('Pick at least one tag');
      return;
    }
    const input = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      tags: form.tags,
    };
    try {
      if (isEdit && collection) {
        const { data } = await updateCollection({ variables: { id: collection.id, input } });
        const r = data?.updateCustomCollection;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Collection updated');
      } else {
        const { data } = await createCollection({ variables: { input } });
        const r = data?.createCustomCollection;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Collection created');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Name *</label>
        <input
          className="input-field mt-1 w-full"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Summer Premiums"
          autoFocus
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Description</label>
        <textarea
          className="input-field mt-1 w-full h-[68px] py-2"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Tags *</label>
          <span className="text-[10px] text-text3">
            {form.tags.length} selected
          </span>
        </div>
        {tags.length === 0 ? (
          <div className="text-xs text-text3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No tags yet. Add tags in the <span className="font-bold">Tags</span> tab first.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const selected = form.tags.includes(t.name);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.name)}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-[12px] font-semibold transition
                    ${selected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-text2 border-border hover:border-primary/40'}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: t.color || (selected ? '#fff' : '#94A3B8') }}
                  />
                  #{t.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="flex-1 btn-primary disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create collection'}
        </button>
      </div>
    </div>
  );
};

// ── Custom Products tab ─────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  draft: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-600',
};

const CustomProductsTab: React.FC<{
  products: CustomProductT[];
  loading: boolean;
  error: string | null;
  onCreate: () => void;
  onEdit: (p: CustomProductT) => void;
  onChanged: (m: string) => void;
}> = ({ products, loading, error, onCreate, onEdit, onChanged }) => {
  const [setStatus] = useMutation(SET_CUSTOM_PRODUCT_STATUS);
  const [removeProduct] = useMutation(DELETE_CUSTOM_PRODUCT);
  const [pendingDelete, setPendingDelete] = useState<CustomProductT | null>(null);

  const handleStatus = async (p: CustomProductT, status: 'active' | 'draft' | 'archived') => {
    const { data } = await setStatus({ variables: { id: p.id, status } });
    const r = data?.setCustomProductStatus;
    onChanged(r?.error ? r.message || 'Failed' : r?.message || 'Status updated');
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { data } = await removeProduct({ variables: { id: pendingDelete.id } });
    const r = data?.deleteCustomProduct;
    setPendingDelete(null);
    onChanged(r?.error ? r.message || 'Failed' : r?.message || 'Product removed');
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertCircle size={16} /> Couldn't load products: {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-text3">
          {products.length} {products.length === 1 ? 'product' : 'products'}
        </div>
        <button onClick={onCreate} className="btn-primary flex items-center gap-2">
          <Package size={14} /> New Product
        </button>
      </div>

      {loading && products.length === 0 ? (
        <div className="card p-12 text-center text-text3">Loading products…</div>
      ) : products.length === 0 ? (
        <div className="card p-12 text-center text-text3 flex flex-col items-center gap-3">
          <Package size={28} className="text-text3" />
          <div className="text-sm font-bold text-text">No products yet</div>
          <div className="text-xs text-text3 max-w-xs">
            Add your first product. Tag it so it appears in matching collections, and add variants for sizes / colors.
          </div>
          <button onClick={onCreate} className="btn-primary mt-2 flex items-center gap-2">
            <Package size={14} /> Add your first product
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              className="card p-3 flex flex-col gap-2 hover:border-primary/50 hover:shadow-lg transition-all overflow-hidden"
            >
              <button
                onClick={() => onEdit(p)}
                className="aspect-square rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center hover:opacity-90 transition"
              >
                {p.image ? (
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={28} className="text-text3" />
                )}
              </button>
              <div className="flex-1 min-w-0 px-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-text3 truncate">{p.code}</span>
                  <span className={`pill ${STATUS_COLORS[p.status] || ''} px-2 ml-auto`}>{p.status}</span>
                </div>
                <button onClick={() => onEdit(p)} className="text-left w-full">
                  <h3 className="text-sm font-bold text-text leading-snug line-clamp-2 mb-1 hover:text-primary transition">
                    {p.title}
                  </h3>
                </button>
                <div className="flex items-baseline gap-1.5">
                  {p.minPrice == null ? (
                    <span className="text-sm font-bold text-text3 italic">No price set</span>
                  ) : p.maxPrice != null && p.maxPrice !== p.minPrice ? (
                    <span className="text-base font-black text-primary">
                      {formatPrice(p.minPrice)} – {formatPrice(p.maxPrice)}
                    </span>
                  ) : (
                    <span className="text-base font-black text-primary">{formatPrice(p.minPrice)}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px] text-text3">
                  <span>
                    {p.variants.length} variant{p.variants.length === 1 ? '' : 's'}
                    {p.trackInventory ? ` · Stock ${p.totalStock}` : ''}
                  </span>
                  {p.brand && <span className="truncate ml-2">{p.brand}</span>}
                </div>
                {p.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.tags.slice(0, 3).map((t) => (
                      <span key={t} className="text-[9px] font-bold px-1.5 py-0.5 bg-indigo-50 text-primary rounded">
                        #{t}
                      </span>
                    ))}
                    {p.tags.length > 3 && (
                      <span className="text-[9px] font-bold text-text3">+{p.tags.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-1 pt-2 border-t border-border px-1">
                <button
                  onClick={() => onEdit(p)}
                  className="flex-1 h-8 px-2 rounded-lg border border-border text-[11px] font-semibold text-text2 hover:bg-gray-50"
                >
                  Edit
                </button>
                {p.status !== 'active' ? (
                  <button
                    onClick={() => handleStatus(p, 'active')}
                    className="h-8 px-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-100"
                  >
                    Publish
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatus(p, 'draft')}
                    className="h-8 px-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-semibold hover:bg-amber-100"
                  >
                    Draft
                  </button>
                )}
                <button
                  onClick={() => setPendingDelete(p)}
                  className="h-8 px-2 rounded-lg border border-border text-red-600 text-[11px] font-semibold hover:bg-red-50"
                >
                  <Trash2Icon />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-base font-bold text-text mb-1">Delete product?</h3>
            <p className="text-xs text-text3 mb-5">
              "{pendingDelete.title}" will be removed permanently.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                className="h-9 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Small inline trash icon (avoid importing again — Trash2 already imported? No.
// Just use a 14px SVG to keep the module's import list stable.)
const Trash2Icon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

// ── Custom Product edit slide-panel ─────────────────────────────
const STATUS_OPTIONS: { id: 'active' | 'draft' | 'archived'; label: string }[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
];

const CustomProductEditPanel: React.FC<{
  product: CustomProductT | null;
  tags: CustomTag[];
  onClose: () => void;
  onSaved: (m: string) => void;
}> = ({ product, tags, onClose, onSaved }) => {
  const isEdit = !!product;
  type Form = {
    title: string;
    description: string;
    code: string;
    barcode: string;
    brand: string;
    productType: string;
    status: 'active' | 'draft' | 'archived';
    mrp: string;
    sellingPrice: string;
    trackInventory: boolean;
    stock: string;
    minimumOrderQty: string;
    image: string;
    gallery: string[];
    tags: string[];
    hasVariants: boolean;
    optionTypes: string[];
    variants: CustomProductVariantT[];
    material: string;
    careInstructions: string;
    gender: string;
    sizeChartUrl: string;
    weight: string;
    dimensions: { length: string; width: string; height: string };
  };

  const initial: Form = {
    title: product?.title || '',
    description: product?.description || '',
    code: product?.code || '',
    barcode: product?.barcode || '',
    brand: product?.brand || '',
    productType: product?.productType || '',
    status: (product?.status as any) || 'draft',
    mrp: product?.mrp != null ? String(product.mrp) : '',
    sellingPrice: product?.sellingPrice != null ? String(product.sellingPrice) : '',
    trackInventory: product?.trackInventory ?? true,
    stock: product?.stock != null ? String(product.stock) : '0',
    minimumOrderQty: product?.minimumOrderQty != null ? String(product.minimumOrderQty) : '1',
    image: product?.image || '',
    gallery: product?.gallery ? [...product.gallery] : [],
    tags: product?.tags ? [...product.tags] : [],
    hasVariants: true,
    optionTypes: product?.optionTypes ? [...product.optionTypes] : [],
    variants: product?.variants ? product.variants.map((v) => ({ ...v, options: v.options.map((o) => ({ ...o })) })) : [],
    material: product?.material || '',
    careInstructions: product?.careInstructions || '',
    gender: product?.gender || '',
    sizeChartUrl: product?.sizeChartUrl || '',
    weight: product?.weight != null ? String(product.weight) : '',
    dimensions: {
      length: product?.dimensions?.length != null ? String(product.dimensions.length) : '',
      width: product?.dimensions?.width != null ? String(product.dimensions.width) : '',
      height: product?.dimensions?.height != null ? String(product.dimensions.height) : '',
    },
  };

  const [form, setForm] = useState<Form>(initial);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'basic' | 'inventory' | 'media' | 'variants' | 'shipping'>('basic');
  const [createProduct, { loading: creating }] = useMutation(CREATE_CUSTOM_PRODUCT);
  const [updateProduct, { loading: updating }] = useMutation(UPDATE_CUSTOM_PRODUCT);
  const saving = creating || updating;

  const toggleTag = (name: string) =>
    setForm((f) =>
      f.tags.includes(name) ? { ...f, tags: f.tags.filter((t) => t !== name) } : { ...f, tags: [...f.tags, name] }
    );

  const setDim = (key: 'length' | 'width' | 'height', value: string) =>
    setForm((f) => ({ ...f, dimensions: { ...f.dimensions, [key]: value } }));

  // Variants helpers
  const setOptionTypeAt = (i: number, value: string) => {
    setForm((f) => {
      const optionTypes = [...f.optionTypes];
      const old = optionTypes[i];
      optionTypes[i] = value;
      // rename in each variant's options
      const variants = f.variants.map((v) => ({
        ...v,
        options: v.options.map((o) => (o.name === old ? { ...o, name: value } : o)),
      }));
      return { ...f, optionTypes, variants };
    });
  };

  const addOptionType = () =>
    setForm((f) => ({ ...f, optionTypes: [...f.optionTypes, `Option ${f.optionTypes.length + 1}`] }));

  const removeOptionType = (i: number) => {
    setForm((f) => {
      const removed = f.optionTypes[i];
      const optionTypes = f.optionTypes.filter((_, idx) => idx !== i);
      const variants = f.variants.map((v) => ({
        ...v,
        options: v.options.filter((o) => o.name !== removed),
      }));
      return { ...f, optionTypes, variants };
    });
  };

  const addVariant = () => {
    setForm((f) => ({
      ...f,
      variants: [
        ...f.variants,
        {
          sku: null,
          barcode: null,
          options: f.optionTypes.map((n) => ({ name: n, value: '' })),
          mrp: null,
          sellingPrice: null,
          discount: 0,
          stock: 0,
          image: null,
          status: true,
        },
      ],
    }));
  };

  const removeVariant = (i: number) =>
    setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const updateVariant = (i: number, patch: Partial<CustomProductVariantT>) =>
    setForm((f) => {
      const variants = [...f.variants];
      variants[i] = { ...variants[i], ...patch };
      return { ...f, variants };
    });

  const updateVariantOption = (vi: number, name: string, value: string) =>
    setForm((f) => {
      const variants = [...f.variants];
      const opts = variants[vi].options.filter((o) => o.name !== name);
      opts.push({ name, value });
      variants[vi] = { ...variants[vi], options: opts };
      return { ...f, variants };
    });

  const submit = async () => {
    setError(null);
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.code.trim()) {
      setError('Product code (SKU) is required');
      return;
    }
    if (form.optionTypes.length === 0) {
      setError('Add at least one option type (e.g. Size)');
      return;
    }
    if (form.variants.length === 0) {
      setError('Add at least one variant');
      return;
    }
    const missingOptionValue = form.variants.find((v) =>
      form.optionTypes.some((opt) => !v.options.find((o) => o.name === opt && o.value))
    );
    if (missingOptionValue) {
      setError('Every variant needs a value for each option type');
      return;
    }

    const num = (s: string) => (s === '' ? null : Number(s));
    const toInt = (s: string) => (s === '' ? null : Math.max(0, Number(s) || 0));

    const input: any = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      code: form.code.trim(),
      barcode: form.barcode.trim() || null,
      brand: form.brand.trim() || null,
      productType: form.productType.trim() || null,
      status: form.status,
      mrp: num(form.mrp),
      sellingPrice: num(form.sellingPrice),
      trackInventory: form.trackInventory,
      stock: toInt(form.stock) ?? 0,
      minimumOrderQty: Math.max(1, Number(form.minimumOrderQty) || 1),
      image: form.image.trim() || null,
      gallery: form.gallery,
      tags: form.tags,
      hasVariants: true,
      optionTypes: form.optionTypes,
      variants: form.variants.map((v) => ({
        sku: v.sku || null,
        barcode: v.barcode || null,
        // Explicitly project name/value so __typename (carried in from the
        // GraphQL response when editing) doesn't leak into the input type.
        options: v.options
          .filter((o) => o.value)
          .map((o) => ({ name: o.name, value: o.value })),
        mrp: v.mrp,
        sellingPrice: v.sellingPrice,
        stock: Math.max(0, Number(v.stock) || 0),
        image: v.image || null,
        status: v.status !== false,
      })),
      material: form.material.trim() || null,
      careInstructions: form.careInstructions.trim() || null,
      gender: form.gender || null,
      sizeChartUrl: form.sizeChartUrl.trim() || null,
      weight: num(form.weight),
      dimensions:
        form.dimensions.length || form.dimensions.width || form.dimensions.height
          ? {
              length: num(form.dimensions.length),
              width: num(form.dimensions.width),
              height: num(form.dimensions.height),
            }
          : null,
    };

    try {
      if (isEdit && product) {
        const { data } = await updateProduct({ variables: { id: product.id, input } });
        const r = data?.updateCustomProduct;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Product updated');
      } else {
        const { data } = await createProduct({ variables: { input } });
        const r = data?.createCustomProduct;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Product created');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-border -mx-6 px-6">
        {([
          { id: 'basic', label: 'Basics' },
          { id: 'inventory', label: 'Price & Stock' },
          { id: 'media', label: 'Media & Tags' },
          { id: 'variants', label: 'Variants' },
          { id: 'shipping', label: 'Shipping' },
        ] as { id: typeof tab; label: string }[]).map((t) => {
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 h-9 text-xs font-bold transition border-b-2 ${
                on ? 'border-primary text-primary' : 'border-transparent text-text2 hover:text-text'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'basic' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <LabelInput
              label="Title *"
              value={form.title}
              onChange={(v) => setForm({ ...form, title: v })}
              autoFocus
            />
          </div>
          <LabelInput label="Code / SKU *" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
          <LabelInput label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          <LabelInput
            label="Product Type"
            value={form.productType}
            onChange={(v) => setForm({ ...form, productType: v })}
            placeholder="Shirt, Dress, Kurta…"
          />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Status</label>
            <select
              className="input-field mt-1 w-full"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as any })}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Description</label>
            <textarea
              className="input-field mt-1 w-full h-[80px] py-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            Variant pricing & stock are managed in the <span className="font-bold">Variants</span> tab.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LabelInput
              label="Min Order Qty"
              value={form.minimumOrderQty}
              onChange={(v) => setForm({ ...form, minimumOrderQty: v })}
              type="number"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.trackInventory}
              onChange={(e) => setForm({ ...form, trackInventory: e.target.checked })}
            />
            Track inventory across variants
          </label>
        </div>
      )}

      {tab === 'media' && (
        <div className="space-y-5">
          <ImageUploader
            label="Primary Image"
            value={form.image || null}
            onChange={(url) => setForm({ ...form, image: url || '' })}
            size="lg"
            path="product"
          />

          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Gallery</label>
            <ImageGalleryUploader
              urls={form.gallery}
              onChange={(urls) => setForm({ ...form, gallery: urls })}
              path="product-gallery"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Tags</label>
              <span className="text-[10px] text-text3">{form.tags.length} selected</span>
            </div>
            {tags.length === 0 ? (
              <div className="text-xs text-text3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                No tags available. Add tags in the <span className="font-bold">Tags</span> tab first.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => {
                  const selected = form.tags.includes(t.name);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTag(t.name)}
                      className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-[12px] font-semibold transition
                        ${selected
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-white text-text2 border-border hover:border-primary/40'}`}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: t.color || (selected ? '#fff' : '#94A3B8') }}
                      />
                      #{t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'variants' && (
        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-800">
            Every product needs at least one option type (e.g. Size) and one variant. Pricing and stock are managed per variant.
          </div>

          <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Option Types</label>
                <div className="mt-2 space-y-2">
                  {form.optionTypes.map((opt, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className="input-field flex-1"
                        value={opt}
                        onChange={(e) => setOptionTypeAt(i, e.target.value)}
                        placeholder="Size / Color / Material"
                      />
                      <button
                        type="button"
                        onClick={() => removeOptionType(i)}
                        className="h-[42px] px-3 rounded-lg border border-border hover:bg-red-50 hover:text-red-600 text-text3"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addOptionType}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    + Add option type
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-text3">Variants</label>
                  <button
                    type="button"
                    onClick={addVariant}
                    disabled={form.optionTypes.length === 0}
                    className="text-xs font-semibold text-primary hover:underline disabled:text-text3 disabled:no-underline"
                  >
                    + Add variant
                  </button>
                </div>
                {form.variants.length === 0 && (
                  <div className="text-xs text-text3 text-center bg-gray-50 border border-border rounded-lg py-6">
                    No variants yet. Add option types above, then add variants.
                  </div>
                )}
                <div className="space-y-3">
                  {form.variants.map((v, vi) => (
                    <div key={vi} className="border border-border rounded-xl p-3 space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        {form.optionTypes.map((opt) => {
                          const found = v.options.find((o) => o.name === opt);
                          return (
                            <div key={opt}>
                              <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                                {opt}
                              </label>
                              <input
                                className="input-field mt-0.5 w-full h-9 text-sm"
                                value={found?.value || ''}
                                onChange={(e) => updateVariantOption(vi, opt, e.target.value)}
                                placeholder="—"
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <LabelInput
                          tiny
                          label="SKU"
                          value={v.sku || ''}
                          onChange={(val) => updateVariant(vi, { sku: val || null })}
                        />
                        <LabelInput
                          tiny
                          label="MRP"
                          value={v.mrp != null ? String(v.mrp) : ''}
                          onChange={(val) => updateVariant(vi, { mrp: val === '' ? null : Number(val) })}
                          type="number"
                        />
                        <LabelInput
                          tiny
                          label="Sell Price"
                          value={v.sellingPrice != null ? String(v.sellingPrice) : ''}
                          onChange={(val) =>
                            updateVariant(vi, { sellingPrice: val === '' ? null : Number(val) })
                          }
                          type="number"
                        />
                        <LabelInput
                          tiny
                          label="Stock"
                          value={String(v.stock)}
                          onChange={(val) => updateVariant(vi, { stock: Number(val) || 0 })}
                          type="number"
                        />
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
                        <ImageUploader
                          label="Variant Image"
                          value={v.image}
                          onChange={(url) => updateVariant(vi, { image: url })}
                          size="sm"
                          path="product-variant"
                        />
                        <div className="flex items-end gap-2 justify-end pb-1">
                          <label className="flex items-center gap-1.5 text-[11px] font-semibold text-text2">
                            <input
                              type="checkbox"
                              checked={v.status !== false}
                              onChange={(e) => updateVariant(vi, { status: e.target.checked })}
                            />
                            Active
                          </label>
                          <button
                            type="button"
                            onClick={() => removeVariant(vi)}
                            className="h-8 px-2 rounded-lg border border-border text-red-600 text-[11px] font-semibold hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
        </div>
      )}

      {tab === 'shipping' && (
        <div className="grid grid-cols-2 gap-3">
          <LabelInput
            label="Weight (grams)"
            value={form.weight}
            onChange={(v) => setForm({ ...form, weight: v })}
            type="number"
          />
          <div />
          <LabelInput
            label="Length (cm)"
            value={form.dimensions.length}
            onChange={(v) => setDim('length', v)}
            type="number"
          />
          <LabelInput
            label="Width (cm)"
            value={form.dimensions.width}
            onChange={(v) => setDim('width', v)}
            type="number"
          />
          <LabelInput
            label="Height (cm)"
            value={form.dimensions.height}
            onChange={(v) => setDim('height', v)}
            type="number"
          />
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="flex gap-2 pt-3 sticky bottom-0 bg-white border-t border-border -mx-6 px-6 py-3">
        <button
          onClick={onClose}
          disabled={saving}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2"
        >
          Cancel
        </button>
        <button onClick={submit} disabled={saving} className="flex-1 btn-primary disabled:opacity-60">
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}
        </button>
      </div>
    </div>
  );
};

const LabelInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  prefix?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  tiny?: boolean;
}> = ({ label, value, onChange, type = 'text', placeholder, prefix, autoFocus, disabled, tiny }) => (
  <div>
    <label
      className={`font-bold uppercase tracking-widest text-text3 ${tiny ? 'text-[9px]' : 'text-[10px]'}`}
    >
      {label}
    </label>
    <div className="relative mt-1">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text3 text-sm">{prefix}</span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        className={`input-field w-full ${prefix ? 'pl-6' : ''} ${tiny ? 'h-9 text-sm' : ''} ${
          disabled ? 'opacity-60' : ''
        }`}
      />
    </div>
  </div>
);

